import { Tag, type TagVariant } from '@dev-spendesk/grapes';
import { format } from 'date-fns';
import compact from 'lodash/compact';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import { type ReactNode } from 'react';
import { Trans } from 'react-i18next';

import { UserAvatar } from 'modules/users/components';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';
import { formatMoney } from 'src/core/utils/money';

import {
  type TGlobalFunctionTyped,
  type I18nKey,
} from '../common/hooks/useTranslation';

export const getPaymentCreationDetails = (
  user: string,
  userId: string,
  arePointOfSaleDetailsProvided: string,
  pointOfSale: string,
  is3DSecure: boolean | string,
  chipUsage: string,
): string | ReactNode => {
  if (
    !arePointOfSaleDetailsProvided ||
    !['physical', 'online', 'atm'].includes(pointOfSale)
  ) {
    return (
      <Trans
        i18nKey={i18nKey('paymentCreatedBy')}
        values={{ user }}
        components={{
          user: <User id={userId} name={user} />,
        }}
      />
    );
  }

  if (pointOfSale === 'online') {
    if (is3DSecure === true) {
      return (
        <Trans
          i18nKey={i18nKey('onlinePaymentMadeWith3DSecureBy')}
          values={{ user }}
          components={{
            user: <User id={userId} name={user} />,
          }}
        />
      );
    }

    if (is3DSecure === false) {
      return (
        <Trans
          i18nKey={i18nKey('onlinePaymentMadeWithout3DSecureBy')}
          values={{ user }}
          components={{
            user: <User id={userId} name={user} />,
          }}
        />
      );
    }

    return (
      <Trans
        i18nKey={i18nKey('onlinePaymentMadeBy')}
        values={{ user }}
        components={{
          user: <User id={userId} name={user} />,
        }}
      />
    );
  }

  if (pointOfSale === 'atm') {
    return (
      <Trans
        i18nKey={i18nKey('atmWithdrawalBy')}
        values={{ user }}
        components={{
          user: <User id={userId} name={user} />,
        }}
      />
    );
  }

  if (pointOfSale === 'physical' && chipUsage) {
    switch (chipUsage) {
      case 'contactless':
        return (
          <Trans
            i18nKey={i18nKey('pointOfSalePaymentMadeContactlessBy')}
            values={{ user }}
            components={{
              user: <User id={userId} name={user} />,
            }}
          />
        );

      case 'pin':
        return (
          <Trans
            i18nKey={i18nKey('pointOfSalePaymentMadeWithPinBy')}
            values={{ user }}
            components={{
              user: <User id={userId} name={user} />,
            }}
          />
        );

      case 'pinAndContactless':
        return (
          <Trans
            i18nKey={i18nKey('pointOfSalePaymentMadeContactlessWithPinBy')}
            values={{ user }}
            components={{
              user: <User id={userId} name={user} />,
            }}
          />
        );

      case 'applePay':
        return (
          <Trans
            i18nKey={i18nKey('pointOfSalePaymentMadeWithApplePayBy')}
            values={{ user }}
            components={{
              user: <User id={userId} name={user} />,
            }}
          />
        );

      case 'googlePay':
        return (
          <Trans
            i18nKey={i18nKey('pointOfSalePaymentMadeWithGooglePayBy')}
            values={{ user }}
            components={{
              user: <User id={userId} name={user} />,
            }}
          />
        );
      default:
        return (
          <Trans
            i18nKey={i18nKey('pointOfSalePaymentMadeBy')}
            values={{ user }}
            components={{
              user: <User id={userId} name={user} />,
            }}
          />
        );
    }
  }

  return (
    <Trans
      i18nKey={i18nKey('pointOfSalePaymentMadeBy')}
      values={{ user }}
      components={{
        user: <User id={userId} name={user} />,
      }}
    />
  );
};

export type TimelineEvents =
  | 'submittedBy'
  | 'expenseMadeBy'
  | 'reimbursedOutsideSpendesk'
  | 'reimbursedFromSpendesk'
  | 'validatedBy'
  | 'paymentCreatedBy'
  | 'atmWithdrawalBy'
  | 'onlinePaymentMadeBy'
  | 'onlinePaymentMadeWith3DSecureBy'
  | 'onlinePaymentMadeWithout3DSecureBy'
  | 'pointOfSalePaymentMadeContactlessBy'
  | 'pointOfSalePaymentMadeWithPinBy'
  | 'pointOfSalePaymentMadeContactlessWithPinBy'
  | 'pointOfSalePaymentMadeWithApplePayBy'
  | 'pointOfSalePaymentMadeWithGooglePayBy'
  | 'pointOfSalePaymentMadeBy'
  | 'unValidatedBy'
  | 'exportedBy'
  | 'attachedInvoiceBy'
  | 'amountRequested'
  | 'userAttachedReceipt'
  | 'amountEditedBy'
  | 'amountChanged'
  | 'amountToReimburse'
  | 'approvedBy'
  | 'deniedBy'
  | 'deniedByController'
  | 'validatedByController'
  | 'amountApproved'
  | 'schedulesUpdatedBy'
  | 'scheduleAddedBy'
  | 'invoiceUpdatedBy'
  | 'invoiceSupplierCreatedBy'
  | 'invoiceSupplierUpdatedBy'
  | 'invoiceSupplierBankInfoCreatedBy'
  | 'invoiceSupplierBankInfoUpdatedBy'
  | 'invoiceSupplierBankDetailsUploadedBy'
  | 'scheduledPaymentPaidExternally'
  | 'scheduledPaymentSentToPayment'
  | 'scheduledPaymentConfirmedByAo'
  | 'scheduledPaymentRejectedByAo'
  | 'scheduledPaymentCancelledByController'
  | 'scheduledPaymentCancelledByControllerNoUser'
  | 'scheduledPaymentPaidByWireTransferInternally'
  | 'wireTransferFailed'
  | 'wireTransferComplianceError'
  | 'wireTransferInvalidAmountGreaterThanLimitError'
  | 'wireTransferWrongBankInfo'
  | 'wireTransferConfirmed'
  | 'paymentScheduleUpdatedBy'
  | 'paymentScheduledOutsideSpendeskBy'
  | 'paymentScheduledSentForConfirmation'
  | 'paymentScheduledCancelled'
  | 'paymentScheduledCancelledNoUser'
  | 'paymentScheduledCancellationInitiated'
  | 'paidFromSpendesk'
  | 'cardWithLastFourDigits'
  | 'sentBackToReview'
  | 'expenseBeneficiariesUpdated';

export const timelineEventToI18nKey: Record<TimelineEvents, I18nKey> = {
  submittedBy: 'common.timeline.events.submittedBy',
  expenseMadeBy: 'common.timeline.events.expenseMadeBy',
  reimbursedOutsideSpendesk: 'common.timeline.events.reimbursedOutsideSpendesk',
  reimbursedFromSpendesk: 'common.timeline.events.reimbursedFromSpendesk',
  validatedBy: 'common.timeline.events.validatedBy',
  paymentCreatedBy: 'common.timeline.events.paymentCreatedBy',
  atmWithdrawalBy: 'common.timeline.events.atmWithdrawalBy',
  onlinePaymentMadeBy: 'common.timeline.events.onlinePaymentMadeBy',
  onlinePaymentMadeWith3DSecureBy:
    'common.timeline.events.onlinePaymentMadeWith3DSecureBy',
  onlinePaymentMadeWithout3DSecureBy:
    'common.timeline.events.onlinePaymentMadeWithout3DSecureBy',
  pointOfSalePaymentMadeContactlessBy:
    'common.timeline.events.pointOfSalePaymentMadeContactlessBy',
  pointOfSalePaymentMadeWithPinBy:
    'common.timeline.events.pointOfSalePaymentMadeWithPinBy',
  pointOfSalePaymentMadeContactlessWithPinBy:
    'common.timeline.events.pointOfSalePaymentMadeContactlessWithPinBy',
  pointOfSalePaymentMadeWithApplePayBy:
    'common.timeline.events.pointOfSalePaymentMadeWithApplePayBy',
  pointOfSalePaymentMadeWithGooglePayBy:
    'common.timeline.events.pointOfSalePaymentMadeWithGooglePayBy',
  pointOfSalePaymentMadeBy: 'common.timeline.events.pointOfSalePaymentMadeBy',
  unValidatedBy: 'common.timeline.events.unValidatedBy',
  exportedBy: 'common.timeline.events.exportedBy',
  attachedInvoiceBy: 'common.timeline.events.attachedInvoiceBy',
  amountRequested: 'common.timeline.events.amountRequested',
  userAttachedReceipt: 'common.timeline.events.userAttachedReceipt',
  amountEditedBy: 'common.timeline.events.amountEditedBy',
  amountChanged: 'common.timeline.events.amountChanged',
  amountToReimburse: 'common.timeline.events.amountToReimburse',
  approvedBy: 'common.timeline.events.approvedBy',
  deniedBy: 'common.timeline.events.deniedBy',
  deniedByController: 'common.timeline.events.deniedByController',
  validatedByController: 'common.timeline.events.validatedByController',
  amountApproved: 'common.timeline.events.amountApproved',
  schedulesUpdatedBy: 'common.timeline.events.schedulesUpdatedBy',
  scheduleAddedBy: 'common.timeline.events.scheduleAddedBy',
  invoiceUpdatedBy: 'common.timeline.events.invoiceUpdatedBy',
  invoiceSupplierCreatedBy: 'common.timeline.events.invoiceSupplierCreatedBy',
  invoiceSupplierUpdatedBy: 'common.timeline.events.invoiceSupplierUpdatedBy',
  invoiceSupplierBankInfoCreatedBy:
    'common.timeline.events.invoiceSupplierBankInfoCreatedBy',
  invoiceSupplierBankInfoUpdatedBy:
    'common.timeline.events.invoiceSupplierBankInfoUpdatedBy',
  invoiceSupplierBankDetailsUploadedBy:
    'common.timeline.events.invoiceSupplierBankDetailsUploadedBy',
  scheduledPaymentPaidExternally:
    'common.timeline.events.scheduledPaymentPaidExternally',
  scheduledPaymentSentToPayment:
    'common.timeline.events.scheduledPaymentSentToPayment',
  scheduledPaymentConfirmedByAo:
    'common.timeline.events.scheduledPaymentConfirmedByAo',
  scheduledPaymentRejectedByAo:
    'common.timeline.events.scheduledPaymentRejectedByAo',
  scheduledPaymentCancelledByController:
    'common.timeline.events.scheduledPaymentCancelledByController',
  scheduledPaymentCancelledByControllerNoUser:
    'common.timeline.events.scheduledPaymentCancelledByControllerNoUser',
  scheduledPaymentPaidByWireTransferInternally:
    'common.timeline.events.scheduledPaymentPaidByWireTransferInternally',
  wireTransferFailed: 'common.timeline.events.wireTransferFailed',
  wireTransferComplianceError:
    'common.timeline.events.wireTransferComplianceError',
  wireTransferInvalidAmountGreaterThanLimitError:
    'common.timeline.events.wireTransferInvalidAmountGreaterThanLimitError',
  wireTransferWrongBankInfo: 'common.timeline.events.wireTransferWrongBankInfo',
  wireTransferConfirmed: 'common.timeline.events.wireTransferConfirmed',
  paymentScheduleUpdatedBy: 'common.timeline.events.paymentScheduleUpdatedBy',
  paymentScheduledOutsideSpendeskBy:
    'common.timeline.events.paymentScheduledOutsideSpendeskBy',
  paymentScheduledSentForConfirmation:
    'common.timeline.events.paymentScheduledSentForConfirmation',
  paymentScheduledCancelled: 'common.timeline.events.paymentScheduledCancelled',
  paymentScheduledCancelledNoUser:
    'common.timeline.events.paymentScheduledCancelledNoUser',
  paymentScheduledCancellationInitiated:
    'common.timeline.events.paymentScheduledCancellationInitiated',
  paidFromSpendesk: 'common.timeline.events.paidFromSpendesk',
  cardWithLastFourDigits: 'common.timeline.events.cardWithLastFourDigits',
  sentBackToReview: 'common.timeline.events.sentBackToReview',
  expenseBeneficiariesUpdated:
    'common.timeline.events.expenseBeneficiariesUpdated',
};

export const i18nKey = (key: TimelineEvents): string =>
  timelineEventToI18nKey[key];

export const formatTimelineEvents = (
  trans: TGlobalFunctionTyped,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  events: any[] | readonly any[],
  request: {
    amount_declared: number;
    amount_to_refund: number;
    currency_declared: string;
  } | null,
  companyCurrency: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: any,
) => {
  const t = (key: TimelineEvents, params = {}) =>
    trans(timelineEventToI18nKey[key], { ...params, ...context });

  return compact(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    map(events, (e) => {
      if (isNil(e)) {
        return;
      }

      const user = get(e, 'user.fullname') ?? '';
      const userId = get(e, 'user.id') ?? '';
      const arePointOfSaleDetailsProvided = get(e, 'pos') ?? false;
      const is3DSecure = get(e, 'pos.is3DSecure') ?? '';
      const pointOfSale = get(e, 'pos.pointOfSale') ?? '';
      const chipUsage = get(e, 'pos.chipUsage') ?? '';
      const { data } = e;

      let firstLine;
      let secondLine;
      let autoApprovalReason;
      switch (e.action_type) {
        case 'request.create': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('submittedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          secondLine = t('amountRequested', {
            amount: formatMoney(
              // make compatible with old event records
              data?.amountDeclared ?? request?.amount_declared,
              data?.currency ?? request?.currency_declared,
            ),
          });
          if (e.status === 'approved' && data?.autoApprovalReason) {
            autoApprovalReason = data.autoApprovalReason;
          }
          break;
        }
        case 'request.approve': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('approvedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
                status: <Status variant="success" />,
              }}
            />
          );
          if (request?.amount_to_refund) {
            secondLine = t('amountApproved', {
              amount: formatMoney(
                // make compatible with old event records
                data?.amountApproved ??
                  data?.amountToRefund ??
                  request.amount_to_refund,
                data?.currency ?? companyCurrency,
              ),
            });
          }
          break;
        }
        case 'request.deny': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('deniedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
                status: <Status variant="alert" />,
              }}
            />
          );
          break;
        }
        case 'request.reject_by_controller': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('deniedByController')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
                status: <Status variant="alert" />,
              }}
            />
          );
          break;
        }
        case 'request.validate_by_controller': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('validatedByController')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
                status: <Status variant="success" />,
              }}
            />
          );
          break;
        }
        case 'request.sent_back_to_review': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('sentBackToReview')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'request.invoice': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('userAttachedReceipt')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'request.updateAmount':
        case 'expense.updateAmount': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('amountEditedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          if (!isEmpty(data)) {
            const { toAmount, fromAmount, toCurrency, fromCurrency } = data;
            secondLine = t('amountChanged', {
              fromAmount: formatMoney(fromAmount, fromCurrency),
              toAmount: formatMoney(toAmount, toCurrency),
            });
          }
          break;
        }
        case 'expense.updateAmountToRefund': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('amountEditedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          if (data?.to) {
            secondLine = t('amountToReimburse', {
              amount: formatMoney(data.to, companyCurrency),
            });
          }
          break;
        }
        case 'expense.made': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('expenseMadeBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'expense.reimbursed': {
          if (data && data.from === 'outside') {
            firstLine = (
              <Trans
                i18nKey={i18nKey('reimbursedOutsideSpendesk')}
                values={{ user }}
                components={{
                  user: <User id={userId} name={user} />,
                  status: <Status variant="success" />,
                }}
              />
            );
            break;
          }
          if (data && data.from === 'spendesk') {
            firstLine = (
              <Trans
                i18nKey={i18nKey('reimbursedFromSpendesk')}
                context={context}
                values={{ user }}
                components={{
                  user: <User id={userId} name={user} />,
                  status: <Status variant="success" />,
                }}
              />
            );

            break;
          }
          break;
        }
        case 'payment.create': {
          firstLine = getPaymentCreationDetails(
            user,
            userId,
            arePointOfSaleDetailsProvided,
            pointOfSale,
            is3DSecure,
            chipUsage,
          );

          if (data?.cardLastFourDigits) {
            secondLine = (
              <Trans
                i18nKey={i18nKey('cardWithLastFourDigits')}
                values={{ lastFourDigits: data.cardLastFourDigits }}
              />
            );
          }

          break;
        }
        case 'payment.invoice': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('attachedInvoiceBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'payment.validate': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('validatedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
                status: <Status variant="success" />,
              }}
            />
          );
          break;
        }
        case 'payment.undoValidate': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('unValidatedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
                status: <Status variant="carbon" />,
              }}
            />
          );
          break;
        }
        case 'payment.export': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('exportedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'invoice.paidFromSpendesk': {
          const { amount, currency } = data;
          firstLine = (
            <Trans
              i18nKey={i18nKey('paidFromSpendesk')}
              values={{
                amount: formatMoney(amount, currency),
              }}
              components={{
                status: <Status variant="success" />,
              }}
            />
          );
          break;
        }
        case 'invoice_request.updated': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('invoiceUpdatedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          if (data?.from && data?.to) {
            const {
              amount_declared: fromAmount,
              currency_declared: fromCurrency,
            } = get(data, 'from', {});
            const { amount_declared: toAmount, currency_declared: toCurrency } =
              get(data, 'to', {});
            if (fromAmount !== toAmount || fromCurrency !== toCurrency) {
              secondLine = t('amountChanged', {
                fromAmount: formatMoney(fromAmount, fromCurrency),
                toAmount: formatMoney(toAmount, toCurrency),
              });
            }
          }
          break;
        }
        case 'invoice_request.updateSchedules': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('schedulesUpdatedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'invoice_request.addSchedule': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('scheduleAddedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'invoice_request.transferScheduling.paymentScheduleUpdated': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('paymentScheduleUpdatedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'invoice_request.transferScheduling.payment.scheduled': {
          if (!data || !data.scheduledAmount || !data.paymentMethod) {
            return null;
          }

          const { scheduledAmount, paymentMethod } = data;
          const formattedScheduledAmount = formatMonetaryValue(scheduledAmount);
          const executionDate = format(new Date(data.executionDate), 'PP');

          if (data.from === 'outside') {
            firstLine = (
              <Trans
                i18nKey={i18nKey('paymentScheduledOutsideSpendeskBy')}
                values={{
                  scheduledAmount: formattedScheduledAmount,
                  user,
                  executionDate,
                }}
                context={paymentMethod}
                components={{
                  user: <User id={userId} name={user} />,
                }}
              />
            );
          } else if (data.from === 'spendesk') {
            firstLine = (
              <Trans
                i18nKey={i18nKey('paymentScheduledSentForConfirmation')}
                values={{
                  scheduledAmount: formattedScheduledAmount,
                  user,
                  executionDate,
                }}
                context={paymentMethod}
                components={{
                  user: <User id={userId} name={user} />,
                }}
              />
            );
          }
          break;
        }
        case 'invoice_request.transferScheduling.payment.cancelled': {
          firstLine = user ? (
            <Trans
              i18nKey={i18nKey('paymentScheduledCancelled')}
              values={{
                scheduledAmount: formatMonetaryValue(data.scheduledAmount),
                user,
              }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          ) : (
            <Trans
              i18nKey={i18nKey('paymentScheduledCancelledNoUser')}
              values={{
                scheduledAmount: formatMonetaryValue(data.scheduledAmount),
              }}
            />
          );
          break;
        }
        case 'request.payment.cancellationInitiated':
        case 'scheduled_payment.payment.cancellationInitiated': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('paymentScheduledCancellationInitiated')}
              values={{
                user,
              }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'invoice_supplier.create': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('invoiceSupplierCreatedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'invoice_supplier.update': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('invoiceSupplierUpdatedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'invoice_supplier_bank_info.create': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('invoiceSupplierBankInfoCreatedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'invoice_supplier_bank_info.update': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('invoiceSupplierBankInfoUpdatedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'invoice_supplier.uploadBankDetails': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('invoiceSupplierBankDetailsUploadedBy')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'scheduled_payment.paid_externally': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('scheduledPaymentPaidExternally')}
              components={{
                status: <Status variant="success" />,
              }}
            />
          );
          break;
        }
        case 'request.scheduled_payment.sent_to_payment':
        case 'scheduled_payment.sent_to_payment': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('scheduledPaymentSentToPayment')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );
          break;
        }
        case 'request.wire_transfer.confirmed_by_ao':
        case 'scheduled_payment.confirmed_by_ao': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('scheduledPaymentConfirmedByAo')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
                status: <Status variant="success" />,
              }}
            />
          );
          break;
        }
        case 'scheduled_payment.rejected_by_ao': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('scheduledPaymentRejectedByAo')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
                status: <Status variant="alert" />,
              }}
            />
          );
          break;
        }
        case 'request.wire_transfer.cancelled_by_controller':
        case 'scheduled_payment.cancelled_by_controller': {
          firstLine = user ? (
            <Trans
              i18nKey={i18nKey('scheduledPaymentCancelledByController')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
                status: <Status variant="alert" />,
              }}
            />
          ) : (
            <Trans
              i18nKey={i18nKey('scheduledPaymentCancelledByControllerNoUser')}
              components={{
                status: <Status variant="alert" />,
              }}
            />
          );
          break;
        }
        case 'transfer_scheduling_payment.paid_by_wire_transfer_internally':
        case 'scheduled_payment.paid_by_wire_transfer_internally': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('scheduledPaymentPaidByWireTransferInternally')}
              components={{
                status: <Status variant="success" />,
              }}
            />
          );
          break;
        }
        case 'request.wire_transfer.failed':
        case 'transfer_scheduling_payment.wire_transfer_failed':
        case 'invoice_request.transferScheduling.payment.wire_transfer_failed':
        case 'scheduled_payment.wire_transfer_failed': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('wireTransferFailed')}
              components={{
                status: <Status variant="alert" />,
              }}
            />
          );
          if (data) {
            if (
              [data.error, data.errorMessage].includes(
                'PaymentComplianceFailure',
              )
            ) {
              secondLine = t('wireTransferComplianceError');
            }
            if (data.error === 'InvalidAmountGreaterThanLimit') {
              secondLine = t('wireTransferInvalidAmountGreaterThanLimitError');
            }
          }
          break;
        }
        case 'invoice_request.transferScheduling.payment.wrong_bank_info':
        case 'scheduled_payment.wrong_bank_info': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('wireTransferFailed')}
              components={{
                status: <Status variant="alert" />,
              }}
            />
          );
          if (data?.errorCode) {
            secondLine = t('wireTransferWrongBankInfo', {
              errorCode: data.errorCode,
            });
          }
          break;
        }

        case 'invoice_request.transferScheduling.payment.confirmed': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('wireTransferConfirmed')}
              values={{
                user,
                amount: formatMonetaryValue(data.scheduledAmount),
              }}
              components={{
                user: <User id={userId} name={user} />,
                status: <Status variant="success" />,
              }}
            />
          );
          break;
        }

        case 'expenseBeneficiaries.updated': {
          firstLine = (
            <Trans
              i18nKey={i18nKey('expenseBeneficiariesUpdated')}
              values={{ user }}
              components={{
                user: <User id={userId} name={user} />,
              }}
            />
          );

          break;
        }

        default:
          return null;
      }

      if (!firstLine) {
        return null;
      }

      return {
        id: e.id,
        firstLine,
        secondLine,
        autoApprovalReason,
        occuredAt: e.occuredAt ?? e.occured_at,
      };
    }),
  );
};

/**
 * Components
 */

export const User = ({
  id,
  name,
  children,
}: {
  id: string;
  name: string;
  children?: ReactNode;
}) => {
  return (
    <span className="title-m flex items-center gap-4">
      <UserAvatar name={name} id={id} size={16} /> {children}
    </span>
  );
};

export const Status = ({
  variant = 'success',
  children,
}: {
  children?: ReactNode;
  variant?: TagVariant;
}) => {
  return (
    <span className="flex items-center gap-4">
      <Tag
        variant={variant}
        iconName={variant === 'success' ? 'circle-check' : 'hexagone-cross'}
      >
        {children}
      </Tag>
    </span>
  );
};
